import React, { useState, useEffect, useRef } from 'react';
import { initiateCall } from '../services/api';
import socket from '../services/socket';
import LiveCallStatus from '../components/LiveCallStatus';
import { buildDialoutPhoneParts, fullDialoutNumber } from '../utils/phoneIndia';

const ACTION_LABELS = {
  visit_branch: 'Visit branch',
  send_link: 'Send KYC link (WhatsApp)',
  callback_agent: 'Callback / agent assistance',
};

function DialOut() {
  const [form, setForm] = useState({ employee_name: '', phone: '', voice: 'female' });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [activeCallId, setActiveCallId] = useState(null);
  const [liveSteps, setLiveSteps] = useState([]);
  const [isCallActive, setIsCallActive] = useState(false);
  const bottomRef = useRef(null);

  const addStep = (type, message) => {
    const time = new Date().toLocaleTimeString('en-US', { hour12: false, hour: '2-digit', minute: '2-digit', second: '2-digit' });
    setLiveSteps((prev) => [...prev, { type, message, time }]);
  };

  useEffect(() => {
    const handleCallUpdate = (data) => {
      if (activeCallId && data.callId === activeCallId) {
        const status = data.status;
        if (status === 'dialing') addStep('dialing', 'Dialing...');
        else if (status === 'ringing') addStep('ringing', 'Ringing...');
        else if (status === 'answered') addStep('answered', 'Call picked up');
        else if (status === 'language_menu_played') addStep('language_menu_played', 'Language menu played');
        else if (status === 'kyc_pending_played') addStep('kyc_pending_played', 'KYC pending message played');
        else if (status === 'confirmation_played') addStep('confirmation_played', 'Customer confirmation prompt played');
        else if (status === 'validation_played') addStep('validation_played', 'Last 4 digit validation prompt played');
        else if (status === 'due_message_played') addStep('due_message_played', 'KYC due message played');
        else if (status === 'action_menu_played') addStep('action_menu_played', 'KYC action menu played');
        else if (status === 'branch_selected' || status === 'link_requested' || status === 'callback_requested') {
          addStep('action', `Action selected: ${ACTION_LABELS[data.call?.selected_action] || data.call?.selected_action || status}`);
        }
        else if (status === 'no_answer') {
          addStep('failed', 'No answer, retry scheduled in background');
        }
        else if (status === 'retrying') {
          addStep('dialing', `Retrying call (${data.connect_retry_attempts}/3)`);
        }
        else if (status === 'unable_to_connect') {
          addStep('failed', 'Marked as unable to connect after max retries');
          setIsCallActive(false);
        }
        else if (status === 'menu_timeout') {
          addStep('timeout', `Waiting for input...`);
        }
        else if (status === 'failed') {
          addStep('failed', 'Call failed');
          setIsCallActive(false);
        }
      }
    };

    const handleDTMF = (data) => {
      if (activeCallId && data.callId === activeCallId) {
        addStep('dtmf', `User pressed: ${data.digit}`);
      }
    };

    const handleCompleted = (data) => {
      if (activeCallId && data.callId === activeCallId) {
        addStep('completed', 'Call completed');
        setIsCallActive(false);
      }
    };

    socket.on('call_update', handleCallUpdate);
    socket.on('dtmf_received', handleDTMF);
    socket.on('call_completed', handleCompleted);

    return () => {
      socket.off('call_update', handleCallUpdate);
      socket.off('dtmf_received', handleDTMF);
      socket.off('call_completed', handleCompleted);
    };
  }, [activeCallId]);

  useEffect(() => {
    if (bottomRef.current) bottomRef.current.scrollIntoView({ behavior: 'smooth' });
  }, [liveSteps]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    setLiveSteps([]);
    setIsCallActive(false);

    try {
      const phoneParts = buildDialoutPhoneParts(form.phone);
      if (!phoneParts.phone) {
        setError('Enter a valid mobile number');
        setLoading(false);
        return;
      }
      const payload = {
        employee_name: form.employee_name,
        ...phoneParts,
        voice: form.voice,
      };
      const res = await initiateCall(payload);
      const callId = res.data.call._id;
      setActiveCallId(callId);
      setIsCallActive(true);
      addStep('dialing', `Call initiated to ${fullDialoutNumber(phoneParts)}`);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to initiate call');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      <h4 className="fw-bold mb-1">Dial Out</h4>
      <p className="text-muted mb-4">Initiate a pending KYC notification call</p>

      <div className="row g-4">
        <div className="col-lg-5">
          <div className="card dialer-card">
            <div className="card-body p-4">
              <h5 className="card-title mb-4">
                <i className="bi bi-telephone-plus me-2 text-primary"></i>
                Start KYC Call
              </h5>

              {error && (
                <div className="alert alert-danger py-2">
                  <i className="bi bi-exclamation-circle me-1"></i> {error}
                </div>
              )}

              <form onSubmit={handleSubmit}>
                <div className="mb-3">
                  <label className="form-label fw-semibold">Employee Name</label>
                  <input
                    type="text"
                    className="form-control form-control-lg"
                    placeholder="e.g. Rajesh Kumar"
                    value={form.employee_name}
                    onChange={(e) => setForm({ ...form, employee_name: e.target.value })}
                    required
                    disabled={loading}
                  />
                </div>
                <div className="mb-3">
                  <label className="form-label fw-semibold">Mobile number</label>
                  <input
                    type="tel"
                    className="form-control form-control-lg"
                    placeholder="e.g. 9876543210 or 919876543210"
                    value={form.phone}
                    onChange={(e) => setForm({ ...form, phone: e.target.value.replace(/\D/g, '') })}
                    required
                    disabled={loading}
                    inputMode="numeric"
                    autoComplete="tel"
                    title="10-digit mobile, or include 91 for full number"
                  />
                  <div className="form-text">Uses India (+91). Add digits only; if you already include 91, it will not be added twice.</div>
                </div>
                <div className="mb-4">
                  <label className="form-label fw-semibold">IVR voice</label>
                  <select
                    className="form-select form-select-lg"
                    value={form.voice}
                    onChange={(e) => setForm({ ...form, voice: e.target.value })}
                    disabled={loading}
                  >
                    <option value="female">Female</option>
                    <option value="male">Male</option>
                  </select>
                </div>
                <button type="submit" className="btn btn-primary btn-lg w-100" disabled={loading}>
                  {loading ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2"></span>
                      Initiating...
                    </>
                  ) : (
                    <>
                      <i className="bi bi-telephone-outbound me-2"></i>
                      Start KYC Call
                    </>
                  )}
                </button>
              </form>
            </div>
          </div>
        </div>

        <div className="col-lg-7">
          <LiveCallStatus steps={liveSteps} isActive={isCallActive} />
          <div ref={bottomRef}></div>
        </div>
      </div>
    </div>
  );
}

export default DialOut;
